import isNil from 'lodash/isNil';

import { coreConfigurationFactory } from '@/api/utils/auth.provider';
import { FpsApi, TimingAPIModel } from '@/generated/api/fps/core';
import { BankId, MessageId, MoneyType, PhoneType, TransactionId } from '@/model/common';
import { Timing, WalletId } from '@/model/withdrawal/types';
import { normalizeDateTime } from '@/utils/api';

import { ReceiverBankFromAPI } from './types';

const fpsApi = new FpsApi(coreConfigurationFactory('auth'));

const parseMoney = (amount?: string): MoneyType => (!isNil(amount) ? Math.round(parseFloat(amount) * 100) : 0);

export const parseTiming = ({ nextAllowedAt, expiresAt }: TimingAPIModel, res: Response): Timing => ({
  nextAllowedAt: normalizeDateTime(nextAllowedAt, { res }),
  expiresAt: normalizeDateTime(expiresAt, { res }),
});

const convertMoney = (amount?: MoneyType): string => (!isNil(amount) ? (amount / 100).toFixed(2) : '');

export const discoverBanks = async ({
  phone,
  walletId,
}: {
  phone: PhoneType;
  walletId?: WalletId;
}): Promise<{ messageId: MessageId; banks: ReceiverBankFromAPI[]; timing: Timing; priorityBank?: BankId }> => {
  const response = await fpsApi.fpsDiscoverBanksRaw({
    newBanksDiscoveryAPIModel: {
      phone: `7${phone}`,
      accountId: walletId,
    },
  });
  const value = await response.value();
  return {
    ...value,
    priorityBank: (value.banks || []).filter(({ name }) => name.includes('(по умолчанию)'))[0]?.id,
    banks: value.banks || [],
    timing: parseTiming(value.timing, response.raw),
  };
};

export const prepareTransfer = async ({
  messageId,
  bankId,
  amount,
  description,
}: {
  messageId: MessageId;
  bankId: BankId;
  amount: MoneyType;
  description?: string;
}): Promise<{ messageId: MessageId; amount: MoneyType; fee: MoneyType; timing: Timing }> => {
  const response = await fpsApi.fpsPrepareTransferRaw({
    newPreparedFpsTransferAPIModel: {
      messageId,
      bankId,
      amount: convertMoney(amount),
      description,
    },
  });
  const value = await response.value();
  return {
    ...value,
    amount: parseMoney(value.amount),
    fee: parseMoney(value.fee),
    timing: parseTiming(value.timing, response.raw),
  };
};

export const getReceiverName = async ({ messageId }: { messageId: MessageId }): Promise<{ name: string }> =>
  fpsApi.fpsGetReceiverName({ messageId });

export const transfer = async ({
  messageId,
  walletId,
  amount,
}: {
  messageId: MessageId;
  walletId: WalletId;
  amount: MoneyType;
}): Promise<{ messageId: string; sbpId?: string; operationId: TransactionId }> =>
  fpsApi.fpsTransfer({
    messageId,
    accountId: walletId,
    amount: convertMoney(amount),
    createOnly: '1',
  });
